import {
  addDays,
  ageYearsOn,
  type BodyType,
  bodyDerived,
  deviceTimeZone,
  ecwShare,
  localDateString,
  type Sex,
} from '@gramas/shared'
import { Button, List, ListRow, SectionLabel, Sheet } from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import { useState } from 'react'
import type { LocalBodyMeasurement } from '../../db/dexie.js'
import { bodyMeasurementsBetween } from '../../db/repo.js'
import { useProfile } from '../useProfile.js'
import { closeBodySheet, useBodySheet } from './bodySheetStore.js'
import { WeightSheet } from './WeightSheet.js'

/**
 * The whole panel, with what each figure means.
 *
 * Tendencijos shows four body rows and stops, because a list of eleven numbers
 * with units is a wall. This is where the rest lives: every figure the scale's
 * own app shows that can be computed from what is stored, its move since the
 * previous complete reading, and one plain sentence under each saying what it
 * is — including, for the ones that are formulas rather than measurements,
 * that they are. Opened from the weight card, from the Svoris row, and by the
 * tap on her push.
 */

const fmt = (value: number, digits = 1) => value.toFixed(digits).replace('.', ',')

/** "+0,3 kg nuo praeito" / "−0,2 nuo praeito", or the honest first-time line. */
function since(now: number | null, before: number | null, unit: string, digits = 1): string {
  if (now == null) return ''
  if (before == null) return 'pirmas matavimas'
  const delta = now - before
  if (Math.abs(delta) < 0.5 / 10 ** digits) return 'be pokyčio nuo praeito'
  const sign = delta > 0 ? '+' : '−'
  return `${sign}${fmt(Math.abs(delta), digits)}${unit ? ` ${unit}` : ''} nuo praeito`
}

/**
 * Xiaomi's nine classes, said as two plain observations instead of a verdict.
 * The grid is fat band against muscle band, so the words are exactly that.
 */
const FAT_WORD = ['riebalų daugiau nei normos', 'riebalų norma', 'riebalų mažiau nei normos']
const MUSCLE_WORD = ['raumenų mažiau nei normos', 'raumenų norma', 'raumenų daugiau nei normos']
const TYPE_ORDER: readonly BodyType[] = [
  'obese',
  'overweight',
  'thick_set',
  'lack_exercise',
  'balanced',
  'balanced_muscular',
  'skinny',
  'balanced_skinny',
  'skinny_muscular',
]
export function bodyTypeWords(type: BodyType): string {
  const index = TYPE_ORDER.indexOf(type)
  return `${FAT_WORD[Math.floor(index / 3)]} · ${MUSCLE_WORD[index % 3]}`
}

/** Subtitle parts joined with a middle dot, empties dropped. */
const sub = (...parts: ReadonlyArray<string | null | undefined>) =>
  parts.filter((part) => part != null && part !== '').join(' · ')

const hasComposition = (m: LocalBodyMeasurement) => m.bodyFatPct != null && m.weightKg != null

export function BodySheetHost() {
  const open = useBodySheet((s) => s.open)
  const [editOpen, setEditOpen] = useState(false)
  const { profile } = useProfile()
  const today = localDateString(new Date(), deviceTimeZone())

  const rows = useLiveQuery(() => bodyMeasurementsBetween(addDays(today, -365), today), [today], [])
  const latest = [...(rows ?? [])].reverse().find((m) => m.weightKg != null) ?? null
  const withComposition = (rows ?? []).filter(hasComposition)
  const comp = withComposition.at(-1) ?? null
  const prev = withComposition.at(-2) ?? null

  const age =
    comp != null && profile?.birthDate ? ageYearsOn(profile.birthDate, comp.measuredOn) : null
  const derived =
    comp?.weightKg != null &&
    comp.bodyFatPct != null &&
    profile?.heightCm != null &&
    age != null &&
    profile.sex != null
      ? bodyDerived({
          weightKg: comp.weightKg,
          bodyFatPct: comp.bodyFatPct,
          muscleKg: comp.muscleKg ?? 0,
          heightCm: profile.heightCm,
          ageYears: age,
          sex: profile.sex as Sex,
        })
      : null
  const prevAge =
    prev != null && profile?.birthDate ? ageYearsOn(profile.birthDate, prev.measuredOn) : null
  const prevDerived =
    prev?.weightKg != null &&
    prev.bodyFatPct != null &&
    profile?.heightCm != null &&
    prevAge != null &&
    profile.sex != null
      ? bodyDerived({
          weightKg: prev.weightKg,
          bodyFatPct: prev.bodyFatPct,
          muscleKg: prev.muscleKg ?? 0,
          heightCm: profile.heightCm,
          ageYears: prevAge,
          sex: profile.sex as Sex,
        })
      : null

  const balance = comp == null ? null : ecwShare(comp.ecwL ?? null, comp.icwL ?? null)
  const prevBalance = prev == null ? null : ecwShare(prev.ecwL ?? null, prev.icwL ?? null)

  const description =
    latest == null
      ? 'Dar nėra nė vieno svėrimo'
      : `${latest.measuredOn} · ${latest.source === 'scale' ? 'atsiuntė svarstyklės' : 'įrašyta ranka'}`

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next) closeBodySheet()
        }}
        title="Kūno sudėtis"
        description={description}
        size="full"
        footer={
          <Button
            block
            size="lg"
            onClick={() => {
              setEditOpen(true)
            }}
          >
            Pataisyti ranka
          </Button>
        }
      >
        {latest == null ? (
          <p className="px-4 py-6 text-sm text-mute">
            Pasisverk ant svarstyklių be kojinių arba įrašyk svorį ranka — čia atsiras visa sudėtis.
          </p>
        ) : (
          <div className="pb-4">
            <div className="px-4 pt-2">
              <div className="tnum font-display text-3xl font-semibold text-ink">
                {fmt(latest.weightKg ?? 0)}{' '}
                <span className="text-lg font-normal text-mute">kg</span>
              </div>
              <p className="tnum mt-0.5 text-xs text-mute">
                {since(
                  latest.weightKg,
                  [...(rows ?? [])]
                    .reverse()
                    .find((m) => m.weightKg != null && m.measuredOn < latest.measuredOn)
                    ?.weightKg ?? null,
                  'kg',
                )}
              </p>
            </div>

            {comp == null ? (
              <p className="px-4 pt-4 text-sm text-mute">
                Kūno sudėties dar nėra. Svarstyklės ją išmatuoja tik basomis kojomis ir stovint iki
                galo; ranka įrašytas svoris jos neturi.
              </p>
            ) : (
              <>
                {comp.id !== latest.id && (
                  <p className="px-4 pt-3 text-xs text-mute">
                    Paskutinis svėrimas be sudėties — kojinės arba ranka. Žemiau — {comp.measuredOn}
                    , paskutinis pilnas.
                  </p>
                )}

                <SectionLabel>Sudėtis</SectionLabel>
                <List className="border-y border-line bg-surface">
                  <ListRow
                    title="Kūno riebalai"
                    wrapSubtitle
                    subtitle={sub(
                      derived == null
                        ? null
                        : `norma ${derived.fatNormalPct.from}–${derived.fatNormalPct.to} %`,
                      since(comp.bodyFatPct, prev?.bodyFatPct ?? null, ''),
                    )}
                    value={fmt(comp.bodyFatPct ?? 0)}
                    meta="%"
                  />
                  <ListRow
                    title="Raumenys"
                    wrapSubtitle
                    subtitle={since(comp.muscleKg ?? null, prev?.muscleKg ?? null, 'kg', 2)}
                    value={comp.muscleKg == null ? '—' : fmt(comp.muscleKg, 1)}
                    meta="kg"
                  />
                  {derived != null && (
                    <ListRow
                      title="Liesoji masė"
                      wrapSubtitle
                      subtitle={sub(
                        'viskas, kas ne riebalai',
                        since(derived.leanMassKg, prevDerived?.leanMassKg ?? null, 'kg', 2),
                      )}
                      value={fmt(derived.leanMassKg, 1)}
                      meta="kg"
                    />
                  )}
                  {derived != null && (
                    <ListRow
                      title="Kaulai"
                      wrapSubtitle
                      subtitle="mineralinė kaulų masė, pagal liesąją masę"
                      value={fmt(derived.boneKg, 1)}
                      meta="kg"
                    />
                  )}
                  <ListRow
                    last
                    title="Baltymai"
                    wrapSubtitle
                    subtitle={sub(
                      'apie 19,5 % liesosios masės',
                      since(comp.proteinPct ?? null, prev?.proteinPct ?? null, ''),
                    )}
                    value={comp.proteinPct == null ? '—' : fmt(comp.proteinPct)}
                    meta="%"
                  />
                </List>

                <SectionLabel>Vanduo</SectionLabel>
                <List className="border-y border-line bg-surface">
                  <ListRow
                    title="Vanduo"
                    wrapSubtitle
                    subtitle={sub(
                      'liesas audinys yra ~73 % vandens',
                      since(comp.waterPct ?? null, prev?.waterPct ?? null, ''),
                    )}
                    value={comp.waterPct == null ? '—' : fmt(comp.waterPct)}
                    meta="%"
                  />
                  {comp.ecwL != null && comp.icwL != null ? (
                    <>
                      <ListRow
                        title="Už ląstelių"
                        wrapSubtitle
                        subtitle={sub(
                          'kraujas, limfa, tarpląstelinis skystis',
                          since(comp.ecwL, prev?.ecwL ?? null, 'l'),
                        )}
                        value={fmt(comp.ecwL)}
                        meta="l"
                      />
                      <ListRow
                        title="Ląstelėse"
                        wrapSubtitle
                        subtitle={sub(
                          'vanduo raumenų ir organų ląstelėse',
                          since(comp.icwL, prev?.icwL ?? null, 'l'),
                        )}
                        value={fmt(comp.icwL)}
                        meta="l"
                      />
                      <ListRow
                        last
                        title="Balansas"
                        wrapSubtitle
                        subtitle={sub(
                          'dalis už ląstelių, norma 37–39 %',
                          since(balance, prevBalance, ''),
                        )}
                        value={balance == null ? '—' : fmt(balance)}
                        meta="%"
                      />
                    </>
                  ) : (
                    <ListRow
                      last
                      title="Balansas"
                      wrapSubtitle
                      subtitle="reikia abiejų dažnių — atsiranda su naujesniu tilteliu"
                      value="—"
                    />
                  )}
                </List>

                {derived != null && (
                  <>
                    <SectionLabel>Apykaita</SectionLabel>
                    <List className="border-y border-line bg-surface">
                      <ListRow
                        title="Bazinė apykaita"
                        wrapSubtitle
                        subtitle="pagal liesąją masę (Katch-McArdle); Nustatymuose — pagal ūgį ir svorį"
                        value={`${derived.bmrKcal}`}
                        meta="kcal"
                      />
                      <ListRow
                        title="Metabolinis amžius"
                        wrapSubtitle
                        subtitle="kurio amžiaus vidutinė apykaita atitinka tavo išmatuotą"
                        value={`${derived.metabolicAge}`}
                        meta="m."
                      />
                      <ListRow
                        last
                        title="Kūno tipas"
                        wrapSubtitle
                        // The verdict is the subtitle, not the value: two plain
                        // observations do not fit in a right-hand column, and a
                        // single word for a body is exactly what this avoids.
                        subtitle={sub(bodyTypeWords(derived.bodyType), 'pagal Xiaomi lentelę')}
                      />
                    </List>
                  </>
                )}

                <SectionLabel>Matavimas</SectionLabel>
                <List className="border-y border-line bg-surface">
                  <ListRow
                    title="Varža 50 kHz"
                    subtitle="srovė tarp ląstelių"
                    value={comp.impedanceOhm == null ? '—' : `${comp.impedanceOhm}`}
                    meta="Ω"
                  />
                  <ListRow
                    title="Varža 250 kHz"
                    subtitle="srovė ir per ląsteles"
                    value={comp.impedanceHighOhm == null ? '—' : `${comp.impedanceHighOhm}`}
                    meta="Ω"
                  />
                  <ListRow
                    last
                    title="Juosmuo"
                    subtitle="matuojamas ranka"
                    value={latest.waistCm == null ? '—' : fmt(latest.waistCm)}
                    meta="cm"
                  />
                </List>
              </>
            )}

            <p className="px-4 pt-4 text-xs text-mute">
              Svarstyklės matuoja svorį ir srovės varžą per kojas; visa kita — formulės. Vienas
              rytas yra triukšmas, kryptis per savaites — signalas. Sverkis tuo pačiu metu,
              nevalgęs, be kojinių.
            </p>
          </div>
        )}
      </Sheet>
      <WeightSheet
        open={editOpen}
        date={today}
        current={
          latest == null
            ? null
            : {
                weightKg: latest.measuredOn === today ? latest.weightKg : null,
                bodyFatPct: latest.measuredOn === today ? latest.bodyFatPct : null,
                waistCm: latest.waistCm,
                source: latest.measuredOn === today ? latest.source : undefined,
              }
        }
        onOpenChange={setEditOpen}
      />
    </>
  )
}
